import { IImage } from '@faire/web-api--source/indigofair/data/IImage';
import { IProductFirstCatNavRequest } from '@faire/web-api--source/indigofair/data/IProductFirstCatNavRequest';
import { IQueryFlashSalesRequest } from '@faire/web-api--source/indigofair/data/IQueryFlashSalesRequest';
import { ISearchMakerProductsRequest } from '@faire/web-api--source/indigofair/data/ISearchMakerProductsRequest';
import { SearchMakersWithFiltersSortBy } from '@faire/web-api--source/indigofair/data/SearchMakersWithFiltersSortBy';
import { SortOrder } from '@faire/web-api--source/indigofair/data/SortOrder';

export namespace INavigationLink {
  export enum NavigationType {
    /**
     * for some special nodes in the tree that don't navigate to anywhere
     * trunk-ignore(buf-lint/ENUM_FIRST_VALUE_ZERO): This enum starts at 1 for legacy reasons
     */
    NONE = 'NONE',
    /**
     * navigate to a product result page
     */
    PRODUCT = 'PRODUCT',
    /**
     * navigate to a brand result page
     */
    BRAND = 'BRAND',
    /**
     * navigate to a sub link list page (C2/C3 page on mobile)
     */
    SUBLINK_LIST = 'SUBLINK_LIST',
    /**
     * expand the sub link list instead of navigating to a new page
     */
    SUBLINK_EXPANDED_LIST = 'SUBLINK_EXPANDED_LIST',
    /**
     * navigate to the brand first preorder marketplace, deprecated now that preorder marketplace is product first
     * @deprecated
     */
    APPAREL_BRAND = 'APPAREL_BRAND',
    /**
     * navigate to a sale collection product page
     */
    SALES = 'SALES',
    /**
     * navigate to a special page, Trending on Faire page
     */
    SPECIAL = 'SPECIAL',
    /**
     * navigate to the product first Preorder Marketplace, with a pre-selected category
     * @deprecated
     */
    APPAREL_PRODUCT = 'APPAREL_PRODUCT',
    /**
     * navigate to a carousel list rendering of the provided sublinks
     */
    CAROUSEL_LIST = 'CAROUSEL_LIST',
    /**
     * navigation to a view that allows users to explore preorders across combined seasons, to
     * explore the breadth of products during fashion week across C1/C2s.
     * Figma: https://www.figma.com/file/AeF21BMh8kVZAT1n58GpfQ/FFWv2-%E2%80%93--Retailer-Handoff?node-id=2519%3A225927
     */
    FASHION_EXPLORATION = 'FASHION_EXPLORATION',
    /**
     * navigation is not active for interaction
     */
    INACTIVE = 'INACTIVE',
    /**
     * navigation to a hub page
     */
    HUB = 'HUB',
  }

  export namespace IMakerProductsSort {
    /**
     * Return an instance of IMakerProductsSort with the provided fields. Any field not
     * listed will be populated with an empty value (an empty array,
     * or undefined, whichever is valid for the type of the value.)
     */
    export const build = (
      partial?: Partial<IMakerProductsSort>
    ): IMakerProductsSort =>
      ({
        ...(partial ?? {}),
      }) as IMakerProductsSort;
  }

  /**
   * Used when user navigate from the link -> brand search result -> brand page(sort the brand products)
   */
  export interface IMakerProductsSort {
    sort_by: keyof typeof ISearchMakerProductsRequest.SortBy | undefined;
    sorting_hint: ISearchMakerProductsRequest.ISortingHint | undefined;
  }

  export namespace IMakerSort {
    /**
     * Return an instance of IMakerSort with the provided fields. Any field not
     * listed will be populated with an empty value (an empty array,
     * or undefined, whichever is valid for the type of the value.)
     */
    export const build = (partial?: Partial<IMakerSort>): IMakerSort =>
      ({
        ...(partial ?? {}),
      }) as IMakerSort;
  }

  /**
   * Used when user navigate from the link -> brand search result(sort the brand result)
   */
  export interface IMakerSort {
    sort_by: keyof typeof SearchMakersWithFiltersSortBy | undefined;
    sort_order: keyof typeof SortOrder | undefined;
  }

  export namespace IGroupsToColumn {
    /**
     * Return an instance of IGroupsToColumn with the provided fields. Any field not
     * listed will be populated with an empty value (an empty array,
     * or undefined, whichever is valid for the type of the value.)
     */
    export const build = (
      partial?: Partial<IGroupsToColumn>
    ): IGroupsToColumn =>
      ({
        group_names: [],
        ...(partial ?? {}),
      }) as IGroupsToColumn;
  }

  /**
   * For featured category arrangement
   */
  export interface IGroupsToColumn {
    group_names: Array<string>;
  }

  export enum StyleGroup {
    STYLE_GROUP_UNKNOWN = 'STYLE_GROUP_UNKNOWN',
    /**
     * "New Product" "New Brand" etc. are a group of links with their own style (e.g. bolded)
     */
    HARDCODED_CATEGORY = 'HARDCODED_CATEGORY',
    /**
     * Preorder season year (Summer 2021) or real categories (e.g. Bath)
     */
    LISTED_CATEGORY = 'LISTED_CATEGORY',
    /**
     * Show "Shop All" as a button for apparel C1s
     */
    SHOP_ALL = 'SHOP_ALL',
    /**
     * Tile for the Carousel_V2 NavigationLinks
     */
    TALL_TILE = 'TALL_TILE',
    /**
     * Tile for the original Trending Collection NavigationLinks
     */
    WIDE_TILE = 'WIDE_TILE',
    /**
     * A standalone divider to separate sections, mainly for mobile web
     */
    DIVIDER = 'DIVIDER',
    /**
     * Indicator that the navigation link should be hidden from all clients' views
     * Linking to and navigating to this node its sublinks will still be possible
     * Specifically created to accommodate navigations that should be hidden from regular category menus
     * but remain accessible through specialized routes
     */
    INVISIBLE = 'INVISIBLE',
    /**
     * Indicator that the navigation link is hidden from part of the clients due to ongoing experiments
     * Clients should still call /record-category-node-visibility even if the navigation link is hidden
     */
    INVISIBLE_FOR_ASSIGNMENT = 'INVISIBLE_FOR_ASSIGNMENT',
    /**
     * Deprecated.
     * Was previously used only on desktop-web.
     * All sublinks are shown in a single column under parent.
     * https://www.figma.com/design/4yygDLbVtWTIIYk9JT2QD6/Navigation-to-new-browse-surfaces?node-id=8980-305988&t=f4tWunLPy0TjSyvw-0
     * @deprecated
     */
    LIST_COLUMN = 'LIST_COLUMN',
  }

  export enum Customization {
    CUSTOMIZATION_UNKNOWN = 'CUSTOMIZATION_UNKNOWN',
    /**
     * The right image under level 1 navigation link "New"（"New" tab in category navigation)
     * is customized and different from other right images
     */
    NEW_GROUP_RIGHT_IMAGE = 'NEW_GROUP_RIGHT_IMAGE',
    /**
     * Highlight with events theme icon may apply to certain link during an Faire event.
     */
    EVENTS_THEME_ICON = 'EVENTS_THEME_ICON',
    /**
     * Place header and footer text above/below the image caption
     */
    GIFT_GUIDE_THEME = 'GIFT_GUIDE_THEME',
    /**
     * FFW 2022 pre-event entry point on mobile web
     */
    PRE_EVENT_ENTRY_FFW_2022 = 'PRE_EVENT_ENTRY_FFW_2022',
    /**
     * Fashion week 2022 will have a customized dropdown
     */
    FASHION_WEEK_2022_CUSTOMIZED_DROPDOWN = 'FASHION_WEEK_2022_CUSTOMIZED_DROPDOWN',
    /**
     * Fashion week 2022 will have a customized right image
     */
    FASHION_WEEK_2022_DESKTOP_RIGHT_IMAGE = 'FASHION_WEEK_2022_DESKTOP_RIGHT_IMAGE',
    /**
     * Fashion week 2022 static entry point on mobile web
     */
    FASHION_WEEK_2022_MOBILE_WEB_ENTRY_POINT = 'FASHION_WEEK_2022_MOBILE_WEB_ENTRY_POINT',
    /**
     * Fashion week 2022 L2 links on mobile web background customization
     */
    FASHION_WEEK_2022_MOBILE_WEB_BACKGROUND = 'FASHION_WEEK_2022_MOBILE_WEB_BACKGROUND',
    /**
     * Fashion week 2022 twinkle shows on the left side of navigation link
     */
    FASHION_WEEK_2022_TWINKLE_LEFT = 'FASHION_WEEK_2022_TWINKLE_LEFT',
    /**
     * Fashion week 2022 twinkle shows on the right side of navigation link
     */
    FASHION_WEEK_2022_TWINKLE_RIGHT = 'FASHION_WEEK_2022_TWINKLE_RIGHT',
    /**
     * New Tag next to Plus Size to highlight that the category is newly introduced
     * @deprecated
     */
    PLUS_SIZE_NEW = 'PLUS_SIZE_NEW',
    /**
     * Preserve the order of the link as they appear in the list
     */
    PRESERVE_ORDER = 'PRESERVE_ORDER',
    /**
     * For FE to augment col width when this is present in a C2
     */
    AUGMENT_COLUMN_WIDTHS = 'AUGMENT_COLUMN_WIDTHS',
  }

  export namespace IRequestArguments {
    export namespace IQueryParamsValues {
      /**
       * Return an instance of IQueryParamsValues with the provided fields. Any field not
       * listed will be populated with an empty value (an empty array,
       * or undefined, whichever is valid for the type of the value.)
       */
      export const build = (
        partial?: Partial<IQueryParamsValues>
      ): IQueryParamsValues =>
        ({
          query_params_values: [],
          ...(partial ?? {}),
        }) as IQueryParamsValues;
    }

    export interface IQueryParamsValues {
      query_params_values: Array<string>;
    }

    /**
     * Return an instance of IRequestArguments with the provided fields. Any field not
     * listed will be populated with an empty value (an empty array,
     * or undefined, whichever is valid for the type of the value.)
     */
    export const build = (
      partial?: Partial<IRequestArguments>
    ): IRequestArguments =>
      ({
        query_params: {},
        ...(partial ?? {}),
      }) as IRequestArguments;
  }

  /**
   * This contains all the information that clients will need to make the search requests that are
   * associated with the user clicking this navigation link.
   * The filterKey in this field is only for search purpose, and would not be surfaced to the users.
   * NavigationLink.preselectedFilterKeys are the ones that would be surfaced to the users.
   */
  export interface IRequestArguments {
    query_params: Partial<
      Record<string, INavigationLink.IRequestArguments.IQueryParamsValues>
    >;
  }

  /**
   * Return an instance of INavigationLink with the provided fields. Any field not
   * listed will be populated with an empty value (an empty array,
   * or undefined, whichever is valid for the type of the value.)
   */
  export const build = (partial?: Partial<INavigationLink>): INavigationLink =>
    ({
      sublinks: [],
      image_captions: [],
      groups_to_columns: [],
      preselected_filter_keys: [],
      customizations: [],
      query_params: {},
      redirect_deep_link_paths: [],
      ...(partial ?? {}),
    }) as INavigationLink;
}

/**
 * Link for navigation
 */
export interface INavigationLink {
  /**
   * unique identifier for a link
   */
  unique_identifier: string | undefined;
  navigation_type: keyof typeof INavigationLink.NavigationType | undefined;
  name: string | undefined;
  hero_image: IImage | undefined;
  thumbnail_image: IImage | undefined;
  /**
   * for analytics, i.e. /cat_nav_ink/{event_name}
   */
  event_name: string | undefined;
  sublinks: Array<INavigationLink>;
  /**
   * Provided when available, otherwise null
   */
  parent_unique_identifier: string | undefined;
  /**
   * Serialized data to pass in the search request protos.
   * When new flags are added to the search request protos, logic will be entirely controlled by backend,
   * and there will be no mobile work needed.
   * This is either a ProductFirstCatNavRequest or a SearchMakersWithFiltersRequest
   */
  json_serialized_request_fields: string | undefined;
  /**
   * Required in ProductFirstCatNavRequest for analytics purposes
   * See ClickInfo.containerName.
   */
  container_name: string | undefined;
  /**
   * for FE to easily traverse the tree by matching the whole path without interpreting the url format
   * For regular categories, it should be in format of "/category/{C1_name}/subcategory/{C2_name}/{C3_name}"
   * For preorder categories, it should be in format of "apparel-preorder?preorderKey={filter_option_key}"\
   * Other special hard-coded categories have their own deepLinkPath formats.
   */
  deep_link_path: string | undefined;
  /**
   * Image located at right side of the first level dropdowns
   */
  right_image: IImage | undefined;
  right_image_redirect_url: string | undefined;
  right_image_alt_text: string | undefined;
  /**
   * Message under the image
   */
  image_captions: Array<string>;
  /**
   * Some Categories may have an icon next to its name
   */
  icon: IImage | undefined;
  /**
   * In the Featured dropdown, L3 items are arranged into columns with a special rule
   * provide the rule by stating the location of each group
   */
  groups_to_columns: Array<INavigationLink.IGroupsToColumn>;
  /**
   * This field contains an array of filter keys.
   * It is being used by "Shop by Brand Values" for now to provide pre-selected filter keys.
   * App will only apply these filter keys for the first search request.
   * To clarify: The other FilterKey in NavigationLink.RequestedArgument should be defined by BE,
   * passed through client and sent for the search request as well.
   * The filter key in requestedArgument would not be surfaced to the users,
   * while the preselectedFilterKeys would be surfaced to the users.
   */
  preselected_filter_keys: Array<string>;
  /**
   * Whether this navigationLink is a group header
   */
  is_group_header: boolean | undefined;
  /**
   * Client is able to add an empty line between different categories or bold category names by checking this field
   */
  style_group: keyof typeof INavigationLink.StyleGroup | undefined;
  /**
   * Duplicates unique identifier without populating sublinks for Carousel_V2, is used to navigate when provided.
   */
  linked_unique_identifier: string | undefined;
  /**
   * NavigationLink subtitle to display, e.g. Faire Fashion Week
   */
  subtitle: string | undefined;
  /**
   * Customizations allow client to know if this navigation link need special treatment.
   * One navigation link can have multiple customizations.
   * e.g. "Fashion Week" l1 navigation link ("Fashion Week" tab in category navigation) during ffw
   * has a star beside its name and l3 links are associated by pictures
   * In this case, FashionWeek.customizations = listOf(FFW_NAME_CUSTOMIZATION, FFW_L3_CUSTOMIZATION)
   */
  customizations: Array<keyof typeof INavigationLink.Customization>;
  /**
   * To show a different title from the category name
   */
  title: string | undefined;
  /**
   * To show vertical image on mobile app
   */
  should_show_vertical_image_on_mobile_app: boolean | undefined;
  /**
   * This field is used by the client get query params that would be used in requests to brand search endpoints
   * "filter_keys": "category:Following Brands,based_in:europe,new:true" (various filter keys for makers search)
   * "sort_by":"recently_followed_desc" (SearchBrandsSortBy)
   * Deprecated, use `NavigationLink.RequestArguments.queryParams` instead
   * @deprecated
   */
  query_params: Partial<Record<string, string>>;
  request_arguments: INavigationLink.IRequestArguments | undefined;
  /**
   * Field to support client side redirects. This filed will be in the form of a category deep link path
   * i.e /category/C1/subcategory/C2/C3
   */
  redirect_deep_link_paths: Array<string>;
  path_identifier: string | undefined;
  /**
   * @deprecated
   */
  preorder_key: string | undefined;
  sale_source: keyof typeof IQueryFlashSalesRequest.SearchSource | undefined;
  maker_sort: INavigationLink.IMakerSort | undefined;
  /**
   * Used when user navigate from the link -> product search result(sort the product result)
   */
  product_sort_by: keyof typeof IProductFirstCatNavRequest.SortBy | undefined;
}
